import { spliceUrlParameter } from '@/utils/spliceUrlParameter';
import { isAndroid } from '@/utils/tool';
import loadWechatSdk from '@/utils/wechat';
import { getWechatInfo } from '@/api/customer';
import { useRouter } from 'next/router';
import { FC, memo, useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import useTranslation from 'next-translate/useTranslation';
import { isIOS, isWeixinBrowser } from '@/utils/common/device';
import { appStoreUrl } from '@/utils/common/open-app';
import { isLatinoChannel } from '@/utils/common/latino-app';
/**
 * open app button of default style
 * or use hooks/useDownloadApp
 */

interface Props {
  openPath?: string;
  query?: { [key: string]: string };
  size?: 'small' | 'large';
  text?: string;
}

const APPID = 'wx42d88765c71af904';
const WxOpenLaunchApp: FC<Props> = memo(props => {
  const { openPath, query, size = 'small', text } = props;
  const router = useRouter();
  const { t, lang } = useTranslation('common');
  const launchBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const ref = launchBtn.current;
    if (!ref) return;
    const ready = () => {
      console.log('wx ready');
    };
    const launch = (e: any) => {
      console.log('wx launch', e);
    };
    const error = (e: any) => {
      console.log('wx error detail', e.detail);
      if (e.detail.errMsg === 'launch:fail') {
        router.push('/review/download');
      }
    };
    ref.addEventListener('ready', ready);
    ref.addEventListener('launch', launch);
    ref.addEventListener('error', error);
    return () => {
      ref.removeEventListener('ready', ready);
      ref.removeEventListener('launch', launch);
      ref.removeEventListener('error', error);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /* @ts-ignore */
    getWechatInfo({
      url: window.location.href
    })
      .then(({ object }) => {
        console.log('WechatSDK:', object);
        loadWechatSdk().then(res => {
          /* @ts-ignore */
          !!window.wx &&
            !!window.wx.config &&
            window.wx.config({
              debug: false,
              appId: object.appId,
              timestamp: object.timestamp || 0,
              nonceStr: object.nonceStr,
              signature: object.signature,
              jsApiList: ['checkJsApi'],
              openTagList: ['wx-open-launch-app']
            });
          /* @ts-ignore */
          !!window.wx &&
            !!window.wx.ready &&
            window.wx.ready(() => {
              console.log('WechatSDK: ready');
            });
        });
      })
      .catch(() => {
        console.log(loadWechatSdk, 'error');
      });
  }, []);

  return (
    <div>
      {/* 在安卓微信中，使用微信开放标签wx-open-launch-app需要在微信开放平台将APPID与域名绑定（只支持绑定一个域名），目前只绑定了线上环境域名，只在线上环境能拉起APP */}
      {/* @ts-ignore */}
      <wx-open-launch-app appid={APPID} extinfo={JSON.stringify({ target: openPath, ws: 'openapp' })} ref={launchBtn}>
        <script type="text/wxtag-template">
          <style></style>
          <button
            data-testid="btn-open-app-btn"
            id="launch-btn"
            style={{
              padding: size == 'large' ? '8px 16px' : '4px 10px',
              fontSize: size == 'large' ? '14px' : '12px',
              background: '#009ED0',
              borderRadius: '999px',
              color: '#ffffff',
              border: 'none',
              outline: 'none'
            }}>
            {text || t('video_landing_download_btn')}
          </button>
        </script>
        {/* @ts-ignore */}
      </wx-open-launch-app>
    </div>
  );
});

const OpenAppBtn: React.FC<Props> = props => {
  const { openPath, query, size = 'small', text } = props;
  const callRef = useRef<any>(null);
  const isAndroidMobile = isAndroid();
  const isWxAndorid = isWeixinBrowser() && isAndroid();
  const { t, lang } = useTranslation('common');
  const isLatino = isLatinoChannel();

  useEffect(() => {
    const CallApp = require('callapp-lib');
    const universalHost = process.env.UNIVERSAL_HOST || process.env.NEXT_PUBLIC_UNIVERSAL_HOST;
    const weeeHost = process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST;

    const options = {
      scheme: {
        protocol: isLatino ? 'masgusto' : isAndroidMobile ? 'sayweee' : 'weee'
      },
      universal: {
        host: universalHost
      },
      appstore: appStoreUrl,
      fallback: `${weeeHost}/review/download`,
      logFunc: (status: 'pending' | 'failure') => {
        console.log(status);
      },
      timeout: 3000
    };
    callRef.current = new CallApp(options);
  }, [isAndroidMobile]);

  const handleClick = useCallback(() => {
    // 添加时间戳以避免缓存问题
    const timestamp = new Date().getTime();
    const targetUrl = encodeURIComponent(spliceUrlParameter(openPath ?? '', { ...query, _ts: timestamp }));
    if (isAndroidMobile) {
      callRef.current?.open({
        path: 'weee.app/jump',
        param: {
          target: targetUrl,
          action: 'openapp'
        }
      });
      // console.log(
      //   callRef.current?.generateScheme({
      //     path: 'weee.app/jump',
      //     param: {
      //       target: targetUrl,
      //       action: 'openapp'
      //     }
      //   })
      // );
    } else {
      callRef.current?.open({
        path: openPath ?? '',
        param: {
          ...query,
          _ts: timestamp,
          action: 'openapp'
        }
      });
      // console.log(
      //   callRef.current?.generateScheme({
      //     path: openPath ?? '',
      //     param: {
      //       ...query,
      //       _ts: timestamp,
      //       action: 'openapp'
      //     }
      //   })
      // );
    }
  }, [isAndroidMobile, openPath, query]);

  return (
    <>
      {!isWxAndorid ? (
        /**使用span标签为了避免WxOpenLaunchApp样式错乱 */
        <span
          data-testid="btn-open-app-btn"
          className={twMerge(
            'flex-none cursor-pointer rounded-full w-fit text-btn-primary-fg-default bg-primary-2',
            size == 'small' && 'h-6 !leading-6 px-2.5 enki-body-2xs-strong',
            size == 'large' && 'h-8 !leading-8 px-4 enki-body-sm-medium'
          )}
          onClick={handleClick}>
          {text || t('video_landing_download_btn')}
        </span>
      ) : (
        <WxOpenLaunchApp {...props} />
      )}
    </>
  );
};

export default OpenAppBtn;
